import { HtmlParser } from '@calendly/html-parser'
import { I18n } from '@calendly/i18n'
import { Avatar } from '@calendly/ui/components/avatar'
import { FullStoryExclude, FullStoryMask } from '@calendly/ui/components/fullstory-privacy'
import { IconCalendar } from '@calendly/ui/components/glyphs/IconCalendar'
import { IconClock } from '@calendly/ui/components/glyphs/IconClock'
import { BodyText, LabelText, TitleText } from '@calendly/ui/components/typography'
import { UiIcon } from '@calendly/ui/components/ui-icon'
import { useMainContainerSize } from '@calendly/ui/hooks/main-container-size-context'
import { colorTextSecondary } from '@calendly/ui/theme'

import { summarizeInterval } from 'shared/utils'

import {
  BodyContainer, DateContainer, DateTimeContainer,
  MetadataDot, NameContainer, NameTitle, OwnedByContainer, TimeContainer, TitleClamp,
} from './styles'
import { getParticipantText } from '../../../meetings'


const metadataTextProps = {
  color: colorTextSecondary,
  size: 'small',
  inline: true,
}

const labelMetadataProps = {
  color: colorTextSecondary,
  size: 'medium',
  inline: true,
}

const metadataDotProps = {
  ...labelMetadataProps,
  weight: 'semibold',
}

export const MeetingDetails = ({
  name,
  eventDate,
  eventStartTime,
  eventEndTime,
  timeNotation,
  host,
  invitees,
  source,
  isHostMeeting,
  owner,
}) => {
  const { isSmallMainContainer } = useMainContainerSize()

  // Use the same logic as meetings page to determine what to display
  const participantText = getParticipantText({
    isHostMeeting,
    source,
    invitees,
    host,
  })

  return (
    <BodyContainer>
      <NameContainer>
        <TitleClamp>
          <TitleText level={3} size="medium"><NameTitle>{name}</NameTitle></TitleText>
        </TitleClamp>
        <BodyText {...metadataTextProps}>
          <FullStoryMask>
            {participantText && HtmlParser(participantText)}
          </FullStoryMask>
        </BodyText>
      </NameContainer>
      <DateTimeContainer>
        <DateContainer>
          <UiIcon icon={IconCalendar} color={colorTextSecondary} size="small" title="" />
          <LabelText {...labelMetadataProps}>
            {eventDate}
          </LabelText>
        </DateContainer>
        {!isSmallMainContainer && (
          <>
            <MetadataDot>
              <LabelText {...metadataDotProps}>·</LabelText>
            </MetadataDot>
            <TimeContainer>
              <LabelText {...labelMetadataProps}>
                <UiIcon icon={IconClock} color={colorTextSecondary} size="small" title="" />
                {summarizeInterval({ from: eventStartTime, to: eventEndTime }, timeNotation, false)}
              </LabelText>
            </TimeContainer>
          </>
        )}
        {/* TODO(Kai/Julian/Wick): revisit owner display when external sharing lands — ensure name is available for cross-org viewers */}
        {owner?.displayName && !owner?.isCurrentUserOwner && (
          <>
            <MetadataDot>
              <LabelText {...metadataDotProps}>·</LabelText>
            </MetadataDot>
            <OwnedByContainer data-testid="recap-owned-by">
              <LabelText {...labelMetadataProps}>
                {I18n.t('publisher.meeting_recap.meeting_card.owned_by', { name: '' }).trimEnd()}
              </LabelText>
              <FullStoryExclude>
                <Avatar
                  size="2x-small"
                  src={owner.avatarUrl}
                  letter={owner.avatarLetter}
                  aria-hidden
                />
              </FullStoryExclude>
              <BodyText {...metadataTextProps}>
                <FullStoryMask>{owner.displayName}</FullStoryMask>
              </BodyText>
            </OwnedByContainer>
          </>
        )}
      </DateTimeContainer>
    </BodyContainer>
  )
}
